import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle } from "lucide-react";

/**
 * Branded shadcn-based replacements for `window.prompt` / `window.confirm`.
 * Use these instead of native browser dialogs — the native ones show the
 * ugly "<host> indique" chrome and break the Milkado look & feel.
 */

/**
 * Modal prompt (replaces `window.prompt`).
 * Props:
 *  - open, onClose(), onSubmit(value)
 *  - title, description, placeholder
 *  - defaultValue, kind: "text" | "number" | "textarea"
 *  - min, max                (numeric only)
 *  - required                (default true)
 *  - submitLabel, cancelLabel
 *  - testId                  (optional data-testid)
 *  - validate(value) => string | null   (optional custom validator)
 */
export function PromptModal({
  open,
  onClose,
  onSubmit,
  title,
  description,
  placeholder = "",
  defaultValue = "",
  kind = "text",
  min,
  max,
  required = true,
  submitLabel = "Valider",
  cancelLabel = "Annuler",
  testId,
  validate,
}) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError("");
    }
  }, [open, defaultValue]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const trimmed = kind === "textarea" ? value : String(value).trim();
    if (required && !trimmed) {
      setError("Ce champ est obligatoire.");
      return;
    }
    if (kind === "number") {
      const n = Number(trimmed);
      if (!Number.isFinite(n)) {
        setError("Valeur numérique invalide.");
        return;
      }
      if (typeof min === "number" && n < min) {
        setError(`Valeur minimale : ${min}.`);
        return;
      }
      if (typeof max === "number" && n > max) {
        setError(`Valeur maximale : ${max}.`);
        return;
      }
    }
    if (validate) {
      const msg = validate(trimmed);
      if (msg) {
        setError(msg);
        return;
      }
    }
    onSubmit(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={testId} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#2A0E3C]">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-[#5C4664]">{description}</DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {kind === "textarea" ? (
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              rows={4}
              data-testid={testId ? `${testId}-input` : undefined}
              autoFocus
            />
          ) : (
            <Input
              type={kind === "number" ? "number" : "text"}
              min={min}
              max={max}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              data-testid={testId ? `${testId}-input` : undefined}
              autoFocus
            />
          )}
          {error && (
            <p className="text-sm text-red-600 font-medium" data-testid={testId ? `${testId}-error` : undefined}>
              {error}
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-full"
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              {cancelLabel}
            </Button>
            <Button
              type="submit"
              className="rounded-full bg-[#D81B8C] hover:bg-[#B21571] text-white"
              data-testid={testId ? `${testId}-submit` : undefined}
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal confirm (replaces `window.confirm`).
 * Props:
 *  - open, onClose(), onConfirm()
 *  - title, description
 *  - confirmLabel, cancelLabel
 *  - destructive (bool) — red button + warning icon
 *  - testId
 */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  destructive = false,
  testId,
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={testId} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#2A0E3C] flex items-center gap-2">
            {destructive && <AlertTriangle className="w-5 h-5 text-red-600" />}
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-[#5C4664] whitespace-pre-wrap">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-full"
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            className={`rounded-full text-white ${destructive ? "bg-red-600 hover:bg-red-700" : "bg-[#D81B8C] hover:bg-[#B21571]"}`}
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
